import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Download, Search, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../components/AdminLayout";
import { adminListOrders } from "../lib/api";
import { formatINR } from "../lib/pricing";

const STATUS_OPTIONS = [
  { key: "all", label: "All" },
  { key: "paid", label: "Paid" },
  { key: "created", label: "Created" },
  { key: "failed", label: "Failed" },
];

export default function AdminOrdersPage() {
  const [filters, setFilters] = useState({ status: "all", q: "", from: "", to: "" });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status && filters.status !== "all") params.status = filters.status;
      if (filters.q.trim()) params.q = filters.q.trim();
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      const data = await adminListOrders(params);
      setOrders(data.orders || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = useMemo(() => {
    const revenue = orders.filter((o) => o.status === "paid").reduce((n, o) => n + (o.total_amount || 0), 0);
    return { count: orders.length, revenue };
  }, [orders]);

  const exportCsv = () => {
    if (!orders.length) {
      toast("No orders to export.");
      return;
    }
    const rows = [
      ["Order ID", "Razorpay Order", "Razorpay Payment", "Status", "Customer Name", "Email", "Phone", "Subtotal (INR)", "GST (INR)", "Total (INR)", "Items Count", "Created At"],
      ...orders.map((o) => [
        o.id,
        o.razorpay_order_id || "",
        o.razorpay_payment_id || "",
        o.status || "",
        o.customer_name || "",
        o.customer_email || "",
        o.customer_phone || "",
        String(o.subtotal || 0),
        String(o.gst_amount || 0),
        String(o.total_amount || 0),
        String((o.items || []).length),
        o.created_at || "",
      ]),
    ];
    const csv = rows
      .map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `authoros-orders-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${orders.length} orders.`);
  };

  const update = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">Studio admin</div>
          <h1 className="mt-2 font-serif-display text-[34px] font-medium leading-[0.98] tracking-tight sm:text-[42px] lg:text-[46px]">
            Orders<span className="text-brand">.</span>
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="rounded-md border border-brand-hair px-3 py-2 font-mono text-[10.5px] uppercase tracking-[0.24em] text-brand-ivory/80">
            {totals.count} results · {formatINR(totals.revenue)} paid
          </div>
          <button
            onClick={load}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-brand-hair text-brand-ivory hover:border-brand hover:text-brand"
            aria-label="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={exportCsv}
            className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2.5 font-mono text-[10.5px] uppercase tracking-[0.28em] text-white hover:bg-[#ff5c26]"
          >
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>
      </div>

      {/* Filter row */}
      <div className="mt-8 grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto_auto_auto]">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-mute" />
          <input
            value={filters.q}
            onChange={(e) => update("q", e.target.value)}
            placeholder="Search by order id, name or email"
            className="h-11 w-full rounded-md border border-brand-hair bg-brand-panel px-10 text-[13px] text-brand-ivory placeholder:text-brand-mute focus:border-brand"
          />
        </div>
        <div className="flex overflow-hidden rounded-md border border-brand-hair bg-brand-panel">
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s.key}
              onClick={() => update("status", s.key)}
              className={`px-3 py-2.5 font-mono text-[10.5px] uppercase tracking-[0.22em] ${
                filters.status === s.key ? "bg-brand text-white" : "text-brand-ivory/70 hover:bg-white/[0.04]"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <input
          type="date"
          value={filters.from}
          onChange={(e) => update("from", e.target.value)}
          className="h-11 rounded-md border border-brand-hair bg-brand-panel px-3 text-[13px] text-brand-ivory focus:border-brand"
        />
        <input
          type="date"
          value={filters.to}
          onChange={(e) => update("to", e.target.value)}
          className="h-11 rounded-md border border-brand-hair bg-brand-panel px-3 text-[13px] text-brand-ivory focus:border-brand"
        />
      </div>

      {/* Table */}
      <div className="mt-6 overflow-hidden rounded-xl border border-brand-hair bg-brand-panel">
        <div className="overflow-x-auto">
          <div className="min-w-[860px]">
            <div className="grid grid-cols-[140px_1fr_auto_auto_auto_140px] gap-4 border-b border-brand-hair px-6 py-3 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
              <div>Order ID</div>
              <div>Customer</div>
              <div>Items</div>
              <div>Total</div>
              <div>Status</div>
              <div>Created</div>
            </div>
            {orders.length === 0 ? (
              <div className="px-6 py-10 text-center font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
                {loading ? "Loading orders..." : "No orders match your filters."}
              </div>
            ) : (
              <ul>
                {orders.map((o) => (
                  <li
                    key={o.id}
                    className="grid grid-cols-[140px_1fr_auto_auto_auto_140px] items-center gap-4 border-b border-brand-hair/60 px-6 py-3 text-[13px]"
                  >
                    <div className="truncate font-mono text-[11px] text-brand">{o.id}</div>
                    <div className="min-w-0">
                      <div className="truncate font-serif-display text-[15px] text-brand-ivory">{o.customer_name}</div>
                      <div className="truncate font-mono text-[10px] uppercase tracking-[0.22em] text-brand-mute">{o.customer_email}</div>
                    </div>
                    <div className="font-mono text-[12px] text-brand-ivory/80">{(o.items || []).length}</div>
                    <div className="font-mono text-[12.5px] text-brand-ivory">{formatINR(o.total_amount)}</div>
                    <StatusPill status={o.status} />
                    <div className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-brand-mute">
                      {o.created_at ? new Date(o.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

function StatusPill({ status }) {
  const map = {
    paid: "bg-brand/15 text-brand",
    created: "border border-brand-hair text-brand-ivory/80",
    failed: "bg-destructive/15 text-destructive",
  };
  return (
    <span className={`rounded-md px-2 py-1 text-center font-mono text-[10px] uppercase tracking-[0.22em] ${map[status] || map.created}`}>
      {status || "created"}
    </span>
  );
}
